import { useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore, applyOrgTheme } from '@/stores/orgStore';
import { STORAGE_KEYS } from '@/lib/constants';
import type { OrgMember, Organization } from '@/types/database.types';
import type { OrgTheme } from '@/types/api';
import { captureApplicationError } from '@/lib/monitoring';
import { toast } from '@/components/ui/toast';
import { isOwnerOnboardingContinuationPath } from '@/lib/auth/onboardingRecovery';
import {
  buildOperationalOrganization,
  ORGANIZATION_CONTEXT_SELECT,
  ORGANIZATION_CONTEXT_SELECT_WITHOUT_TIMEZONE,
  isMissingOrganizationTimezoneError,
  type OperationalOrganization,
} from '@/lib/orgContext';

interface OrgProviderProps {
  children: ReactNode;
}

interface OrgMemberRow {
  role: OrgMember['role'];
  is_active: boolean;
  deactivation_effective_at: string | null;
  organization: OperationalOrganization | null;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const user = useAuthStore((s) => s.user);
  // The workspace context reloads on a timer, on focus and on every tab switch. Telling
  // somebody once that their access has ended is news; telling them every minute for as
  // long as they read their own record is harassment.
  const deactivationNoticeShownFor = useRef<string | null>(null);
  const userId = user?.id ?? null;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setOrg = useOrgStore((s) => s.setOrg);
  const setLoading = useOrgStore((s) => s.setLoading);
  const setOrgError = useOrgStore((s) => s.setError);
  const clearOrg = useOrgStore((s) => s.clear);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      clearOrg();
      applyOrgTheme(null);
      return;
    }

    const authenticatedUserId = userId;
    const isCompletingOwnerOnboarding = isOwnerOnboardingContinuationPath(
      window.location.pathname,
      window.location.search,
    );

    let mounted = true;
    let lastRefreshAt = 0;
    let hasLoadedOrgContext = false;

    async function loadOrgContext(options: { background?: boolean } = {}) {
      if (!mounted) return;

      const background = options.background ?? false;

      if (!background) {
        // Never expose a previous tenant context while this authenticated session resolves.
        clearOrg();
        applyOrgTheme(null);
        setLoading(true);
        setOrgError(null);
      }

      try {
        const { data: isPlatformAdmin, error: platformAdminError } =
          await supabase.rpc('is_platform_admin');
        if (platformAdminError) throw platformAdminError;

        if (isPlatformAdmin) {
          // Platform Admin sessions must never inherit a tenant workspace or branding.
          applyOrgTheme(null);
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_ORG_ID);
          clearOrg();
          return;
        }

        await finalizeEffectiveMemberDeactivations();

        const preferredOrgId = localStorage.getItem(STORAGE_KEYS.ACTIVE_ORG_ID);

        // Try preferred org first
        if (preferredOrgId) {
          const preferredResultWithTimezone = await supabase
            .from('org_members')
            .select(
              `
              is_active,
              deactivation_effective_at,
              role,
              organization:organizations(
                ${ORGANIZATION_CONTEXT_SELECT}
              )
            `,
            )
            .eq('user_id', authenticatedUserId)
            .eq('organization_id', preferredOrgId)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();

          const preferredResult = isMissingOrganizationTimezoneError(
            preferredResultWithTimezone.error,
          )
            ? await supabase
              .from('org_members')
              .select(
                `
                is_active,
                deactivation_effective_at,
                role,
                organization:organizations(
                  ${ORGANIZATION_CONTEXT_SELECT_WITHOUT_TIMEZONE}
                )
              `,
              )
              .eq('user_id', authenticatedUserId)
              .eq('organization_id', preferredOrgId)
              .eq('is_active', true)
              .limit(1)
              .maybeSingle()
            : preferredResultWithTimezone;

          const row = preferredResult.data as OrgMemberRow | null;

          if (row?.organization && isMembershipCurrentlyActive(row)) {
            const org = buildOperationalOrganization(row.organization);
            setOrg(org as Organization, row.role);
            applyOrgTheme(org.theme as OrgTheme | null);
            return;
          }

          if (row?.organization && !isMembershipCurrentlyActive(row)) {
            localStorage.removeItem(STORAGE_KEYS.ACTIVE_ORG_ID);
          }

          if (preferredResult.error) {
            captureApplicationError(preferredResult.error, {
              tags: { area: 'organization', operation: 'preferred_org_context' },
              extra: { preferredOrgId },
            });
          }
        }

        // Fallback: most recent active membership
        const fallbackResultWithTimezone = await supabase
          .from('org_members')
          .select(
            `
            is_active,
            deactivation_effective_at,
            role,
            organization:organizations(
              ${ORGANIZATION_CONTEXT_SELECT}
            )
          `,
          )
          .eq('user_id', authenticatedUserId)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(10);

        const fallbackResult = isMissingOrganizationTimezoneError(
          fallbackResultWithTimezone.error,
        )
          ? await supabase
            .from('org_members')
            .select(
              `
              is_active,
              deactivation_effective_at,
              role,
              organization:organizations(
                ${ORGANIZATION_CONTEXT_SELECT_WITHOUT_TIMEZONE}
              )
            `,
            )
            .eq('user_id', authenticatedUserId)
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(10)
          : fallbackResultWithTimezone;

        const { error } = fallbackResult;
        const rows = (fallbackResult.data ?? []) as OrgMemberRow[];
        const activeRow =
          rows.find((row) => row.organization && isMembershipCurrentlyActive(row)) ?? null;

        if (error) {
          handleOrgContextError(error, { background });
          return;
        }

        if (!activeRow?.organization) {
          clearOrg();
          const hasDeactivatedMembership =
            rows.some((row) => row.organization && !isMembershipCurrentlyActive(row)) ||
            (await hasInactiveMembership(authenticatedUserId));

          if (
            hasDeactivatedMembership &&
            !isCompletingOwnerOnboarding &&
            deactivationNoticeShownFor.current !== authenticatedUserId
          ) {
            // Their access to the workspace has ended; their own record has not. Signing
            // them out would take away the payslips, timesheets and shift history they
            // are entitled to keep, so the session stays and the workspace goes.
            //
            // Nothing is left unguarded by this: without an active membership, row level
            // security already returns only what belongs to them personally.
            localStorage.removeItem(STORAGE_KEYS.ACTIVE_ORG_ID);
            setOrgError(null);
            deactivationNoticeShownFor.current = authenticatedUserId;
            toast.info(ORG_ACCESS_DEACTIVATED_MESSAGE, { duration: 10_000 });
          }
          return;
        }

        const org = buildOperationalOrganization(activeRow.organization);
        localStorage.setItem(STORAGE_KEYS.ACTIVE_ORG_ID, org.id);
        setOrg(org as Organization, activeRow.role);
        applyOrgTheme(org.theme as OrgTheme | null);
      } catch (error) {
        handleOrgContextError(error, { background });
      } finally {
        hasLoadedOrgContext = true;
      }
    }

    function handleOrgContextError(error: unknown, options: { background?: boolean } = {}) {
      captureApplicationError(error, {
        tags: { area: 'organization', operation: 'load_org_context' },
        extra: { background: options.background ?? false },
      });

      if (options.background) return;

      clearOrg();
      applyOrgTheme(null);
      setOrgError(ORG_STARTUP_ERROR_MESSAGE);
      toast.error(ORG_STARTUP_ERROR_MESSAGE);
    }

    function refreshOrgContext() {
      if (!mounted) return;

      const now = Date.now();
      if (now - lastRefreshAt < ORG_CONTEXT_REFRESH_THROTTLE_MS) return;

      lastRefreshAt = now;
      void loadOrgContext({ background: hasLoadedOrgContext });
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        refreshOrgContext();
      }
    }

    refreshOrgContext();
    const intervalId = window.setInterval(refreshOrgContext, ORG_CONTEXT_POLL_MS);
    window.addEventListener('focus', refreshOrgContext);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshOrgContext);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, isAuthenticated, setOrg, setLoading, setOrgError, clearOrg]);

  return <>{children}</>;
}

const ORG_STARTUP_ERROR_MESSAGE =
  'We could not load your workspace. Refresh the page, or sign out and back in if the issue continues.';

// Not framed as a failure. Leaving an employer is ordinary, and what is in front of them
// - their payslips, timesheets and shift history - has not gone anywhere.
const ORG_ACCESS_DEACTIVATED_MESSAGE =
  'Your access to this organisation has ended. Your own record stays with you. Contact the business owner or manager if you think this is a mistake.';

const ORG_CONTEXT_REFRESH_THROTTLE_MS = 30_000;
const ORG_CONTEXT_POLL_MS = 60_000;

async function finalizeEffectiveMemberDeactivations() {
  const { error } = await supabase.rpc('finalize_my_effective_member_deactivations');
  if (!error) return;

  const message = error.message?.toLowerCase() ?? '';
  const isMigrationPending =
    message.includes('could not find the function') ||
    message.includes('schema cache') ||
    (message.includes('function public.finalize_my_effective_member_deactivations') &&
      message.includes('does not exist'));

  if (!isMigrationPending) {
    captureApplicationError(error, {
      tags: { area: 'organization', operation: 'finalize_effective_member_deactivations' },
    });
  }
}

async function hasInactiveMembership(userId: string) {
  const { data } = await supabase
    .from('org_members')
    .select('id')
    .eq('user_id', userId)
    .or(`is_active.eq.false,deactivation_effective_at.lte.${new Date().toISOString()}`)
    .limit(1);

  return Array.isArray(data) && data.length > 0;
}

function isMembershipCurrentlyActive(row: OrgMemberRow) {
  if (!row.is_active) return false;
  if (!row.deactivation_effective_at) return true;

  const effectiveAt = new Date(row.deactivation_effective_at);
  if (Number.isNaN(effectiveAt.getTime())) return true;

  return effectiveAt > new Date();
}
